import {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { QuestionCircleOutlined } from '@ant-design/icons';
import { Modal, Form, Input, Tooltip, Button } from 'antd';

import { setCookie } from '../../../../../helpers/utils/cookies';
import {
  getLast7DaysPeriod,
} from '../../../../../helpers/utils/reports';
import {
  calculateWidth,
  showErrorMessage,
} from '../../../../../helpers/utils/ui';
import { normalizeMetricRefs } from '../../../../../shared/common/components/reports/metrics/utils';
import ModalSettings from '../../../../../shared/common/components/reports/ModalSettings';
import { ReportTable } from '../../../../../shared/common/components/reports/report-table/ReportTable';
import { chartDetails } from '../../../../../shared/common/constants/details';
import { useActions } from '../../../../../shared/common/hooks/useActions';
import { useTypedSelector } from '../../../../../shared/common/hooks/useTypedSelector';
import { DashboardBlockProvider } from '../../../context';
import { DashboardComponent, ColorRule, BLOCK_MIN_SIZES, GRID_CONSTANTS } from '../../../types';
import { useBlockModalState } from '../../../hooks/useBlockModalState';
import Params from '../../Params/Params';
import { BlockPreview } from '../components/BlockPreview';
import { BlockSettingsPanel } from '../components/BlockSettingsPanel';
import ColorRulesEditor from '../components/ColorRulesEditor';
import { HeadingStyleEditor } from '../components/HeadingStyleEditor';
import {
  attrItems,
  getReportTypeFromDate,
  chartToTableReportType,
} from '../constants';

interface Props {
  editState: {
    visible: boolean;
    id: string;
    data: any;
  };
  setEditState: React.Dispatch<
    SetStateAction<{ visible: boolean; id: string; data: {} }>
  >;
  onEditBlock: (component: DashboardComponent) => void;
}

const EditBlockModal: React.FC<Props> = ({
  editState,
  setEditState,
  onEditBlock,
}) => {
  const [ form ] = Form.useForm();

  // Общий хук для управления состоянием модального окна
  const {
    // Redux state
    details,
    filters,
    filterParent,
    attributes,
    groupings,
    metrics,
    metricsChecked,
    groupsChecked,
    totalChecked,
    percentages,
    sparklines,
    cellFills,
    analysis,
    cellFilling,
    filtersTable,
    sortTable,
    chartType,
    isAxesVisible,

    // Режим выбора строк
    groupsSelectionMode,
    selectFirstNRows,

    // Локальное состояние
    extraFieldSuffix,
    currentSelect,
    selectedMetrics,
    setSelectedMetrics,
    selectedGroupings,
    setSelectedGroupings,
    dataItemsMetrics,
    dataItemsGroupings,
    listKeysMetrics,
    listKeysGroupings,
    heading,
    setHeading,
    headingStyles,
    setHeadingStyles,
    isBasicMetricsVisible,
    setIsBasicMetricsVisible,
    isBasicGroupingsVisible,
    setIsBasicGroupingsVisible,
    isBlockVisible,
    setIsBlockVisible,
    filtersFilterList,

    // Обработчики
    handlePeriodDateChange,
    handleValueChange,
    handleListChange,
    handleCloseModal,
    applyTableCellSettings,

    // Redux actions
    actions: {
      setGroupings,
      setMetrics,
      setMetricsChecked,
      setGroupingsChecked,
      setFilters,
      setFilterParent,
      setAttributes,
      setDetails,
      setChartType,
      setTotalChecked,
      clearTotalChecked,
      setMetaModalTableFetching,
      setMetaModalChartFetching,
      setPercentages,
      setTableData,
      setIsNotParamsFilterVisible,
      setGroupsSelectionMode,
      setSelectFirstNRows,
      setAxesVisibleValue,
    },
  } = useBlockModalState({
    form,
    mode: {
      type: 'edit',
      blockId: editState.id,
      initialData: {
        ...editState.data?.data,
        componentType: editState.data?.componentType,
      },
    },
    visible: editState.visible,
  });

  // totalChecked из блочного state (для восстановления при открытии модалки)
  const blockTotalChecked = useTypedSelector((state) => {
    const blkId = editState.data?.data?.blockId;
    if (blkId && state.table.blocks[blkId]) {
      return state.table.blocks[blkId].totalChecked;
    }
    return [];
  });

  // Текущее состояние таблицы блока на дашборде: спарклайны и заливку могли поменять после
  // последнего сохранения дашборда, поэтому оно свежее, чем editState.data
  const blockTableState = useTypedSelector((state) => {
    const blkId = editState.data?.data?.blockId;
    return blkId ? state.table.blocks[blkId] : undefined;
  });

  // Места в панели «Группировки / Метрики», куда таблица дорисует кнопки, как в отчёте
  const [ toolbarLeftSlot, setToolbarLeftSlot ] = useState<HTMLElement | null>(null);
  const [ toolbarRightSlot, setToolbarRightSlot ] = useState<HTMLElement | null>(null);

  // Правила условной раскраски числа
  const [ colorRules, setColorRules ] = useState<ColorRule[]>([]);

  // ==================== УНИКАЛЬНАЯ ЛОГИКА EDIT ====================

  // Порядок как в оригинале: сначала setEditState (закрытие и unmount), потом Redux cleanup
  const handleClose = useCallback(() => {
    setEditState({ id: '', visible: false, data: {} });
    handleCloseModal();
  }, [ setEditState, handleCloseModal ]);

  const {
    setAttributesDashboardBlocks,
    setDashboardBlockDetails,
    setDashboardsBlocksTableInitialState,
    setChartType: setBlockChartType,
    setAxesVisibleValue: setBlockAxesVisible,
  } = useActions();

  // Пропсы для CartNumber в предпросмотре
  const cartNumberProps = useMemo(() => ({
    selectedMetrics,
    selectedGroupings,
    metricsChecked,
    attribution: attributes,
    filters,
    chartType,
    groupsChecked,
    isShow: true,
    details,
    colorRules,
  }), [
    selectedMetrics,
    selectedGroupings,
    metricsChecked,
    attributes,
    filters,
    chartType,
    groupsChecked,
    details,
    colorRules,
  ]);

  // Сброс видимости блока при изменении параметров предпросмотра
  useEffect(() => {
    if (isBlockVisible) {
      setIsBlockVisible(false);
    }
  }, [currentSelect, heading, headingStyles, cartNumberProps]);

  // Рендер предпросмотра через общий компонент
  const renderView = useMemo(() => {
    return (
      <BlockPreview
        displayType={currentSelect as any}
        form={form}
        heading={heading}
        headingStyles={headingStyles}
        chartHeight={350}
        maxRows={10}
        cartNumberProps={cartNumberProps}
      />
    );
  }, [
    currentSelect,
    form,
    heading,
    headingStyles,
    cartNumberProps,
  ]);

  const onSubmit = (values: any) => {
    const newBlockId = editState.data.data.blockId;
    const attributionValue =
      typeof attributes?.attribution === 'string'
        ? [ attributes.attribution ]
        : attributes?.attribution;
    const typeApplicationsValue =
      typeof attributes?.type_applications === 'string'
        ? [ attributes.type_applications ]
        : attributes?.type_applications;
    const chartData = {
      details,
      filters: filters,
      filterParent: filterParent,
      attribution: {
        attribution: attributionValue,
        type_applications: typeApplicationsValue,
      },
      chartType,
      isAxesVisible,
    };
    // «Динамика» и заливка всей таблицы — только у блока-таблицы: у графиков скрытая таблица
    // иначе делала бы лишние запросы прошлого периода
    const isTableBlock = currentSelect === 'table';
    const blockAnalysis = isTableBlock && analysis === true;
    const blockCellFilling = isTableBlock && cellFilling === true;
    const isChosenNumberMetric = currentSelect === 'number';
    if (isChosenNumberMetric && metricsChecked.length === 0) {
      showErrorMessage('Выберите метрику в таблице.');
      return;
    }

    setAttributesDashboardBlocks({
      blockId: newBlockId,
      attribution: attributes?.attribution,
      type_applications: attributes?.type_applications,
    });

    setDashboardsBlocksTableInitialState({
      ids: [ newBlockId ],
      data: {
        groupings,
        metrics,
        metricsChecked,
        groupsChecked: groupsSelectionMode !== 'manual' ? [] : groupsChecked,
        totalChecked,
        percentages,
        sparklines,
        cellFills,
        analysis: blockAnalysis,
        cellFilling: blockCellFilling,
        filtersTable,
        sortTable,
        groupsSelectionMode: groupsSelectionMode || 'manual',
        selectFirstNRows: selectFirstNRows ?? null,
      },
    });
    setFilters({ data: filters, blockId: newBlockId });

    // Обновляем chartType per-block БЕЗ сброса series
    // (setDashboardsBlocksChartState заменяет весь блок на initialBlockState с series: [])
    setBlockChartType({ data: chartType, blockId: newBlockId });
    setBlockAxesVisible({ data: isAxesVisible, blockId: newBlockId });
    setDashboardBlockDetails({
      blockId: newBlockId,
      detail: details,
    });
    const getHeight = () => {
      if (currentSelect === 'chart' && chartType === 'pie') {
        return 20;
      }
      if (currentSelect === 'number') {
        return 4;
      }
      return 20;
    };
    const getWidth = () => {
      if (currentSelect === 'number') {
        // Авторасчёт ширины по длине заголовка
        const title = values.title || '';
        const charWidth = 8; // ~8px на символ при font-size: 13px
        const blockPadding = 60; // padding + иконки drag/dropdown
        const titlePx = title.length * charWidth + blockPadding;
        const minValuePx = 190; // минимум для числового значения (до ~7 цифр при 32px bold + суффикс ₽/%)
        const requiredPx = Math.max(titlePx, minValuePx);
        const gridW = Math.ceil(requiredPx / GRID_CONSTANTS.CELL_WIDTH);
        return Math.max(gridW, BLOCK_MIN_SIZES.number.minW);
      }
      return calculateWidth(selectedMetrics.length, 12) * GRID_CONSTANTS.SCALE_X;
    };

    const blockMinSizes = BLOCK_MIN_SIZES[currentSelect] || {
      minW: GRID_CONSTANTS.MIN_BLOCK_WIDTH,
      minH: GRID_CONSTANTS.MIN_BLOCK_HEIGHT,
    };

    const newComponent: DashboardComponent = {
      title: values.title,
      componentType: currentSelect,
      layoutData: {
        i: newBlockId,
        ...editState.data?.layoutData,
        scaled: true,
        minH: blockMinSizes.minH,
        minW: blockMinSizes.minW,
        ...(() => {
          if (editState?.data?.componentType === 'heading') {
            return {
              w: editState.data.layoutData.w,
              h: editState.data.layoutData.h,
            };
          }
          if (
            editState?.data?.layoutData?.w &&
            editState?.data?.layoutData?.h
          ) {
            return {
              w: editState.data.layoutData.w,
              h: editState.data.layoutData.h,
            };
          }
          return {
            w: getWidth(),
            h: getHeight(),
          };
        })(),
      },
      data: {
        blockId: newBlockId,
        selectedMetrics,
        selectedGroupings,
        groupings: selectedGroupings,
        metrics: selectedMetrics,
        metricsChecked,
        groupsChecked: groupsSelectionMode !== 'manual' ? [] : groupsChecked,
        totalChecked,
        groupsSelectionMode: groupsSelectionMode || 'manual',
        selectFirstNRows: selectFirstNRows ?? null,
        sortTable,
        sparklines,
        cellFills,
        analysis: blockAnalysis,
        cellFilling: blockCellFilling,
        heading,
        headingStyles,
        colorRules: colorRules.length > 0 ? colorRules : undefined,
        ...values,
        date: form.getFieldValue('date'),
        ...chartData,
      },
    };
    onEditBlock(newComponent);
    handleClose();
  };

  // Инициализация формы при открытии
  useEffect(() => {
    const setValues = async () => {
      // Очищаем предыдущие данные таблицы
      setTableData({ rows: [], total: 0, groupings: [] });
      // Спарклайны, заливка, «Динамика» и «%» столбцов этого блока — иначе в окне остались бы чужие
      // (из отчёта или прошлого окна) и сохранение перетёрло бы ими блок. «%» раньше обнулялись,
      // и любое сохранение окна снимало проценты у столбцов блока
      const savedBlockData = editState?.data?.data || {};
      const blockPercentages = blockTableState?.percentages ?? savedBlockData.percentages;
      setPercentages(Array.isArray(blockPercentages) ? blockPercentages : []);
      applyTableCellSettings({
        sparklines: blockTableState?.sparklines ?? savedBlockData.sparklines,
        cellFills: blockTableState?.cellFills ?? savedBlockData.cellFills,
        analysis: blockTableState?.analysis ?? savedBlockData.analysis,
        cellFilling: blockTableState?.cellFilling ?? savedBlockData.cellFilling,
      });

      setFilters({ isModal: true, data: editState?.data?.data?.filters || [] });
      setFilterParent({ isModal: true, data: editState?.data?.data?.filterParent || [] });
      setChartType(editState.data?.data?.chartType);
      setAxesVisibleValue(editState.data?.data?.isAxesVisible ?? false);
      setCookie('chosenDisplay', editState.data.componentType, {
        expires: 300,
      });
      form.setFieldValue('display', editState.data.componentType);

      const dateValue = editState.data.data.date || 'reg';
      form.setFieldValue('date', dateValue);

      // Устанавливаем группировки и метрики из сохранённых данных
      const savedGroupings = editState?.data?.data?.selectedGroupings || editState?.data?.data?.groupings || [];
      const savedMetrics = editState?.data?.data?.selectedMetrics || editState?.data?.data?.metrics || [];
      setGroupings({ data: savedGroupings });
      setMetrics({ data: savedMetrics });

      setAttributes(editState.data?.data?.attribution);
      setDetails(editState.data?.data?.details);
      await handlePeriodDateChange(dateValue, true);
      const savedSelectionMode = editState.data?.data?.groupsSelectionMode || 'manual';
      setGroupsSelectionMode(savedSelectionMode);
      setSelectFirstNRows(editState.data?.data?.selectFirstNRows ?? null);
      if (savedSelectionMode === 'manual') {
        setGroupingsChecked(editState.data?.data?.groupsChecked);
      }
      setMetricsChecked({ data: normalizeMetricRefs(editState?.data?.data?.metricsChecked || []) });

      if (editState.data?.data?.colorRules) {
        setColorRules(editState.data.data.colorRules);
      }
      if (currentSelect === 'number') {
        setTotalChecked();
      }
      if (currentSelect === 'chart') {
        // Восстанавливаем totalChecked из блочного state, а не навязываем "Итого и среднее"
        const hasTotalInBlock = blockTotalChecked?.some((t: any) => t.pathStr === 'total');
        if (hasTotalInBlock) {
          setTotalChecked();
        } else {
          clearTotalChecked();
        }
      }

      setMetaModalTableFetching({ value: true });
    };
    setValues();

    return () => {
      setPercentages([]);
      applyTableCellSettings();
      setGroupings({ data: [] });
      setMetrics({ data: [] });
      setGroupingsChecked([]);
      setMetricsChecked({ data: [] });
      setTableData({ rows: [], total: 0, groupings: [] });
      setIsNotParamsFilterVisible(false);
    };
  }, [ editState, form ]);

  // ==================== RENDER ====================

  return (
    <Modal
      style={{
        top: 0,
      }}
      open={editState.visible}
      className={'addBlockModal modal-settings'}
      width={'auto'}
      footer={null}
      maskClosable={false}
      onCancel={handleClose}
    >
      <DashboardBlockProvider
        mode="modal-edit"
        blockId={editState.id}
        componentType={currentSelect}
        date={form.getFieldValue('date')}
      >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ width: '100%', marginRight: '24px' }}>
          <Form
            onFinish={onSubmit}
            layout={'vertical'}
            form={form}
          >
            <div
              style={{
                marginTop: '16px',
                width: '100%',
                display: 'flex',
                columnGap: '8px',
                alignItems: 'flex-end',
                marginBottom: currentSelect === 'heading' ? '20px' : '0',
              }}
            >
              <Form.Item
                initialValue={editState.data.title}
                name="title"
                label={
                  currentSelect === 'heading' ? 'Заголовок' : 'Название блока'
                }
                style={{ flex: currentSelect === 'heading' ? '1' : undefined, marginBottom: 0 }}
              >
                {heading ? (
                  <Input
                    value={heading}
                    onChange={(event) => setHeading(event.target.value)}
                    style={currentSelect === 'heading'
                      ? { fontSize: '16px', fontWeight: 500, height: '40px', borderRadius: '8px' }
                      : { height: '40px', borderRadius: '8px' }}
                  />
                ) : (
                  <Input style={{ height: '40px', borderRadius: '8px' }} />
                )}
              </Form.Item>
              <Form.Item
                initialValue={editState.data.data.description}
                style={{ width: '60%', marginBottom: 0 }}
                name="description"
                label="Описание"
              >
                <Input style={{ height: '40px', borderRadius: '8px' }} />
              </Form.Item>
            </div>

            {currentSelect === 'heading' && (
              <HeadingStyleEditor
                styles={headingStyles}
                onStylesChange={setHeadingStyles}
                previewText={heading || 'Введите текст заголовка'}
              />
            )}
            {!heading && (
              <>
                <BlockSettingsPanel
                  form={form}
                  currentSelect={currentSelect as any}
                  onValueChange={handleValueChange}
                />
              </>
            )}
            {currentSelect !== '' && currentSelect !== 'heading' && (
              <div className="dashboard-params-compact" style={{ marginBottom: '20px' }}>
                <Params
                  attributionItems={attrItems}
                  detailsItems={chartDetails}
                  filtersFilter={filtersFilterList}
                  isFilterVisible={false}
                  isModalDashboard={true}
                />
              </div>
            )}

            {currentSelect === 'heading' ? null : (
              <>
                <div
                  className="universal-table-settings"
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <div style={{ display: 'flex', columnGap: '4px', alignItems: 'center' }}>
                    <Button data-help-id="block-edit-groupings" onClick={() => setIsBasicGroupingsVisible(true)}>
                      Группировки
                    </Button>
                    <Tooltip
                      title={
                        <div
                          className="page-title-tooltip-text"
                          dangerouslySetInnerHTML={{
                            __html:
                              'Группировка — это возможность сгруппировать регистрации пользователей по определённым условиям и посмотреть их данные по 60 метрикам. В SCANе около 70 различных группировок. <a href="https://doc.scananalytics.ru/otchety/gruppirovki/" rel="noopener noreferrer" target="_blank">Подробнее</a>.',
                          }}
                        ></div>
                      }
                      color="#fff"
                      zIndex={9999}
                      trigger="click"
                      overlayClassName="page-title-tooltip"
                    >
                      <QuestionCircleOutlined
                        style={{ color: '#ccc', fontSize: 14, marginLeft: 10 }}
                      />
                    </Tooltip>
                    {/* Сюда таблица дорисует выгрузку */}
                    <span ref={setToolbarLeftSlot} style={{ display: 'flex', alignItems: 'center' }} />
                  </div>
                  <div style={{ display: 'flex', columnGap: '20px', alignItems: 'center' }}>
                    <Button data-help-id="block-edit-metrics" onClick={() => setIsBasicMetricsVisible(true)}>
                      Метрики
                    </Button>
                    <Tooltip
                      placement="bottom"
                      title={
                        <div
                          className="page-title-tooltip-text"
                          dangerouslySetInnerHTML={{
                            __html:
                              'Метрики — это показатель выбранных группировок. В сервисе около 60 разных показателей. <a href="https://doc.scananalytics.ru/otchety/metriki/" rel="noopener noreferrer" target="_blank">Подробнее</a>.',
                          }}
                        ></div>
                      }
                      color="#fff"
                      zIndex={9999}
                      trigger="click"
                      overlayClassName="page-title-tooltip"
                    >
                      <QuestionCircleOutlined
                        style={{ color: '#ccc', fontSize: 14 }}
                      />
                    </Tooltip>
                    {/* Сюда таблица дорисует «Динамику», заливку и копирование */}
                    <span ref={setToolbarRightSlot} style={{ display: 'flex', alignItems: 'center' }} />
                  </div>
                </div>

                <ReportTable
                  reportType={chartToTableReportType(getReportTypeFromDate(form.getFieldValue('date')))}
                  displayMode="preview"
                  isModal={true}
                  date={form.getFieldValue('date')}
                  hideGroupingsPanel={true}
                  hideMetricsPanel={true}
                  hideSelectionControls={currentSelect === 'number' || currentSelect === 'table'}
                  hideTotalCheckbox={currentSelect === 'table'}
                  hideMetricButtons={currentSelect === 'table'}
                  disableGroupsLimit={currentSelect === 'table'}
                  overridePeriod={getLast7DaysPeriod()}
                  toolbarSlots={currentSelect === 'table'
                    ? { left: toolbarLeftSlot, right: toolbarRightSlot }
                    : undefined}
                  showTableSettings={currentSelect === 'table'}
                  getExportFileName={() => form.getFieldValue('title') || ''}
                />

                {currentSelect === 'number' && (
                  <ColorRulesEditor rules={colorRules} onChange={setColorRules} />
                )}
              </>
            )}
          </Form>
        </div>
        {currentSelect !== 'heading' && (
          <div
            style={{
              width: '100%',
              paddingRight: '20px',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fff',
              flex: 1,
              paddingTop: '30px',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                marginBottom: '20px',
                marginRight: '45px',
              }}
            >
              {(selectedMetrics.length > 0 || selectedGroupings.length > 0) && (
                <button
                  data-help-id="block-edit-preview"
                  style={{
                    backgroundColor: '#11487d',
                    color: 'white',
                    width: '170px',
                    height: '45px',
                    borderRadius: '5px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onClick={() => { setIsBlockVisible((prev) => !prev); !isBlockVisible && setMetaModalChartFetching({ value: true }); }}
                >
                  {isBlockVisible ? 'Скрыть предпросмотр' : 'Предпросмотр'}
                </button>
              )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '12px',
                }}
              >
                {(selectedMetrics.length > 0 ||
                  selectedGroupings.length > 0) && (
                  <>
                    <Button
                      data-help-id="block-edit-save"
                      onClick={() => form.submit()}
                      style={{
                        backgroundColor: '#fe3f30',
                        color: 'white',
                        width: '170px',
                        height: '45px',
                        borderRadius: '5px',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      Сохранить и закрыть
                    </Button>

                    <Button
                      data-help-id="block-edit-close"
                      style={{
                        backgroundColor: 'white',
                        color: '#bfbfbf',
                        width: '170px',
                        height: '45px',
                        borderRadius: '5px',
                        border: '1px solid #bfbfbf',
                      }}
                      type="primary"
                      onClick={handleClose}
                      htmlType="submit"
                    >
                      Закрыть
                    </Button>
                  </>
                )}
              </div>
            </div>

            {(
              <Form.Item>
              <div
                style={{
                  textAlign: 'left',
                  marginTop: '20px',
                  display: isBlockVisible ? 'block' : 'none',
                  background: '#f3f3f3',
                  borderRadius: '12px',
                  padding: currentSelect === 'number' ? '0' : '16px',
                  ...(currentSelect === 'table'
                    ? {
                        width: '100%',
                        paddingLeft: '24px',
                        background: '#f3f3f3',
                        borderRadius: '12px',
                      }
                    : {}),
                }}
              >
                  <div style={{ maxHeight: '40%', overflowY: 'hidden' }}>
                    {renderView}
                  </div>
              </div>
              </Form.Item>
            )}
          </div>
        )}
        {(currentSelect === 'heading') && (
                  <div style={{ display: 'flex', gap: '20px', paddingLeft: '70%' }}>
                    <Button
                      data-help-id="block-edit-save"
                      onClick={() => form.submit()}
                      style={{
                        backgroundColor: '#fe3f30',
                        color: 'white',
                        width: '170px',
                        height: '45px',
                        borderRadius: '5px',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      Сохранить и закрыть
                    </Button>

                    <Button
                      data-help-id="block-edit-close"
                      style={{
                        backgroundColor: 'white',
                        color: '#bfbfbf',
                        width: '170px',
                        height: '45px',
                        borderRadius: '5px',
                        border: '1px solid #bfbfbf',
                      }}
                      type="primary"
                      onClick={handleClose}
                      htmlType="submit"
                    >
                      Закрыть
                    </Button>
                  </div>
                )}

        <div style={{ marginTop: '20px' }}></div>
      </div>
      {isBasicMetricsVisible && (
        <ModalSettings
          title={'Метрики'}
          dataItems={dataItemsMetrics}
          listKeys={listKeysMetrics}
          defSelected={selectedMetrics}
          defChecked={selectedMetrics
            .map((m: any) => m?.dataKey ?? m?.key)
            .filter(Boolean)}
          actionSet={(newState) => {
            handleListChange(newState, setSelectedMetrics, setMetrics);
            setMetaModalTableFetching({ value: true });
            if (form.getFieldValue('display') === 'number' && newState.length > 0) {
              const metric = newState[0];
              setMetricsChecked({
                data: [ {
                  dataKey: metric?.dataKey || metric?.key,
                  name: metric?.dataKey || metric?.key,
                  label: metric?.name || metric?.label,
                } ]
              });
              setTotalChecked();
            }
          }}
          isVisible={isBasicMetricsVisible}
          setVisible={setIsBasicMetricsVisible}
          hideTriggerButton
          singleSelect={form.getFieldValue('display') === 'number'}
          isAdReport={form.getFieldValue('date') === 'ad'}
        />
      )}
      {isBasicGroupingsVisible && (
        <ModalSettings
          title={'Группировки'}
          dataItems={dataItemsGroupings}
          listKeys={listKeysGroupings}
          defSelected={selectedGroupings}
          defChecked={selectedGroupings
            .map((g: any) => g?.dataKey ?? g?.key)
            .filter(Boolean)}
          actionSet={(newState) => {
            handleListChange(newState, setSelectedGroupings, setGroupings);
            setMetaModalTableFetching({ value: true });
          }}
          isVisible={isBasicGroupingsVisible}
          setVisible={setIsBasicGroupingsVisible}
          hideTriggerButton
          extraFieldSuffix={extraFieldSuffix}
          isAdReport={form.getFieldValue('date') === 'ad'}
        />
      )}
      </DashboardBlockProvider>
    </Modal>
  );
};

export default EditBlockModal;
